import { Component, Injector, Input, OnInit } from '@angular/core';
import { take } from 'rxjs/operators';
import { WidgetBaseComponent } from '../../widgetBase';
import { RdLocalizedNumberPipe } from '../../../pipes/rd-localized-number.pipe';

/**
 * FrancePropertyAnalysis Widget
 */
@Component({
  selector: 'france-property-analysis-widget',
  templateUrl: './france-property-analysis-widget.component.html',
  styleUrls: ['../../common/css/general.scss','./france-property-analysis-widget.component.scss']
})
export class FrancePropertyAnalysisWidgetComponent extends WidgetBaseComponent implements OnInit {

  /**
   * Widget Guid
   */
  @Input() widgetGuid: string;

  /**
  * Constructor
  */
  constructor(injector: Injector, private rdLocalizedNumberPipe: RdLocalizedNumberPipe) {
    super(injector, 'francePropertyAnalysis', false);
  }

  /**
   * Initializes the component
   */
  ngOnInit(): void {
    this.initWidgetConfig();
    this.widgetResized();
  }

  /**
   * Called by base class to let widget register to various app events (like saving, loading, resizing etc)
   */
  registerEvents(): void {
    this.useDefaultServiceSaveChangesHandler("francePropertyAnalysis", this.provideSaveObject.bind(this));
  }

  /**
   * Called by base class to inform widget that it is initialized
   * Mostly used to define where widget data should be loaded from
   */
  onInitialized(): void {
    this.mockupData$ = this.widgetService.loadMockup('francePropertyAnalysis').pipe(take(1));

    this.widgetData$ = this.mockup ?
      this.mockupData$
      : this.widgetService.load('francePropertyAnalysis', this.getReport()).pipe();

  }

  /**
   * Called by base class to let widget preprocess loaded data if required
   */
  protected preprocessLoadedData(data) {
    data.distributionOfLettableArea = [];
    data.distributionOfRentalIncome = [];

    let areaSum = 0;
    let rentSum = 0;

    for (const entry of data.units) {
      const chartEntryLettableArea = { label: entry.areaType, value: entry.lettableArea };
      const chartEntryRentalIncome = { label: entry.areaType, value: entry.contractualRentYear };

      areaSum += entry.lettableArea;
      rentSum += entry.contractualRentYear;

      data.distributionOfLettableArea.push(chartEntryLettableArea);
      data.distributionOfRentalIncome.push(chartEntryRentalIncome);
    }

    for (const entry of data.parking) {
      const chartEntryRentalIncome = { label: entry.unitType, value: entry.contractualRentYear };
      rentSum += entry.contractualRentYear;

      data.distributionOfRentalIncome.push(chartEntryRentalIncome);
    }

    for (const entry of data.externalArea) {
      const chartEntryLettableArea = { label: entry.areaType, value: entry.lettableArea };
      const chartEntryRentalIncome = { label: entry.areaType, value: entry.contractualRentYear };

      areaSum += entry.lettableArea;
      rentSum += entry.contractualRentYear;

      data.distributionOfLettableArea.push(chartEntryLettableArea);
      data.distributionOfRentalIncome.push(chartEntryRentalIncome);
    }

    for (const entry of data.distributionOfLettableArea) {
      entry.percentage = this.rdLocalizedNumberPipe.transform(entry.value / areaSum * 100, 1, false, '%', '', 0);
    }

    for (const entry of data.distributionOfRentalIncome) {
      entry.percentage = this.rdLocalizedNumberPipe.transform(entry.value / rentSum * 100, 1, false, '%', '', 0);
    }

    return data;
  }

  /**
   * Called by base class in save process. Widget should return object to be saved
   */
  async provideSaveObject(): Promise<any> {
    return this.widgetData;
  }

  /**
   * Called by base class to inform widget about save action result
   * @param _success {boolean} The result of save action
   * @param _err {any} The error object, if save action failed
   */
  saveCompleted(_success: boolean, _err: any = null): void {
    //do something if error
  }

}
